import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const L = {
  it: {
    title: "Post LinkedIn generati",
    sub: "Ogni venerdì alle 8:30 l'assistente AI prepara il post della settimana dall'ultimo articolo pubblicato e te lo invia anche via email. Qui trovi lo storico, pronto da incollare sulla pagina aziendale.",
    empty: "Nessun post generato ancora: il primo arriverà venerdì prossimo.",
    copyIt: "Copia IT",
    copyEn: "Copia EN",
    copied: "Post copiato! Incollalo sulla pagina aziendale LinkedIn.",
    openArticle: "Apri articolo",
    italian: "Versione italiana",
    english: "English version",
    error: "Caricamento non riuscito, riprova",
  },
  en: {
    title: "Generated LinkedIn posts",
    sub: "Every Friday at 8:30 the AI assistant prepares the week's post from the latest published article and emails it to you. Here is the history, ready to paste on the company page.",
    empty: "No posts generated yet: the first one will arrive next Friday.",
    copyIt: "Copy IT",
    copyEn: "Copy EN",
    copied: "Post copied! Paste it on the LinkedIn company page.",
    openArticle: "Open article",
    italian: "Italian version",
    english: "English version",
    error: "Loading failed, try again",
  },
};

export default function LinkedInPanel() {
  const { lang } = useLang();
  const t = L[lang];
  const [posts, setPosts] = useState(undefined);

  useEffect(() => {
    api
      .get("/admin/linkedin/posts")
      .then((r) => setPosts(r.data.posts || []))
      .catch(() => {
        setPosts([]);
        toast.error(t.error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success(t.copied);
  };

  const boxCls =
    "mt-2 max-h-72 overflow-auto whitespace-pre-wrap border border-white/10 bg-white/5 p-4 text-sm leading-relaxed text-ivory/80";
  const copyBtnCls =
    "mt-3 inline-flex items-center gap-2 border border-gold/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ivory/70 transition-colors hover:border-gold/60 hover:text-gold-bright";

  return (
    <div data-testid="linkedin-panel">
      <h2 className="font-serif text-2xl text-ivory">{t.title}</h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ivory/50">{t.sub}</p>

      {posts === undefined ? (
        <p className="mt-10 font-mono text-xs uppercase tracking-[0.2em] text-ivory/40">…</p>
      ) : posts.length === 0 ? (
        <div className="mt-10 border border-white/10 bg-white/5 p-10 text-center text-sm text-ivory/50" data-testid="linkedin-empty">
          {t.empty}
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          {posts.map((p) => (
            <div key={p.id} data-testid="linkedin-post-card" className="border border-white/10 bg-navy-light p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-serif text-lg text-ivory">{p.title_it || p.title_en}</p>
                <span className="border border-gold/40 bg-gold/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-gold-bright">
                  {p.week}
                </span>
              </div>
              <div className="mt-5 grid gap-6 lg:grid-cols-2">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{t.italian}</p>
                  <pre className={boxCls} data-testid="linkedin-post-it">{p.post_it}</pre>
                  <button onClick={() => copy(p.post_it)} data-testid="linkedin-copy-it" className={copyBtnCls}>
                    <Copy className="h-3.5 w-3.5" /> {t.copyIt}
                  </button>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{t.english}</p>
                  <pre className={boxCls} data-testid="linkedin-post-en">{p.post_en}</pre>
                  <button onClick={() => copy(p.post_en)} data-testid="linkedin-copy-en" className={copyBtnCls}>
                    <Copy className="h-3.5 w-3.5" /> {t.copyEn}
                  </button>
                </div>
              </div>
              {p.url && (
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 text-xs text-ivory/50 transition-colors hover:text-gold-bright"
                  data-testid="linkedin-open-article"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> {t.openArticle}
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}